import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { GraduationCap, Trophy, Zap, BookOpen, X, Mail, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { GoogleLogin } from '@react-oauth/google';

const Landing = ({ onLogin }) => {
  // Auth states
  const [authMode, setAuthMode] = useState('social'); // 'social' | 'login' | 'register'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [university, setUniversity] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setName('');
    setUniversity('');
    setError('');
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const isRegister = authMode === 'register';
      const endpoint = isRegister ? '/api/auth/register' : '/api/auth/login';
      const body = isRegister
        ? { email, password, name: name || undefined, university: university || undefined }
        : { email, password };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        const data = await response.json();
        onLogin(data.token, data.user);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Authentication failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential: credentialResponse.credential })
      });

      if (response.ok) {
        const data = await response.json();
        onLogin(data.token, data.user);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Google sign-in failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError('Google sign-in was cancelled or failed. Try email instead.');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
      {/* Background image */}
      <div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1767102060241-130cb9260718?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxzdHVkZW50JTIwc3R1ZHlpbmclMjBsaWJyYXJ5JTIwZm9jdXNlZHxlbnwwfHx8fDE3NzAzMzUyODF8MA&ixlib=rb-4.1.0&q=85')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      />

      <div className="relative z-10 container mx-auto px-6 py-12 min-h-screen flex items-center">
        <div className="grid md:grid-cols-2 gap-12 items-center w-full">
          {/* Left — Branding */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="text-white"
          >
            <h1 className="text-7xl font-black tracking-tighter leading-none mb-6">
              GradeGuard AI
            </h1>
            <p className="text-xl text-slate-300 mb-8 leading-relaxed">
              Transform your study materials into interactive quizzes. Battle concepts, earn XP, and level up from C-Student to Dean's List.
            </p>

            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
                <GraduationCap className="w-10 h-10 text-accent mb-3" />
                <h3 className="font-bold text-lg mb-2">Smart Learning</h3>
                <p className="text-sm text-slate-300">AI-powered quiz generation from PDFs, PPTs & YouTube</p>
              </div>
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
                <Trophy className="w-10 h-10 text-yellow-400 mb-3" />
                <h3 className="font-bold text-lg mb-2">Boss Battles</h3>
                <p className="text-sm text-slate-300">Defeat concepts with 10 perfect answers</p>
              </div>
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
                <Zap className="w-10 h-10 text-accent mb-3" />
                <h3 className="font-bold text-lg mb-2">XP & Levels</h3>
                <p className="text-sm text-slate-300">Track progress and maintain streaks</p>
              </div>
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
                <BookOpen className="w-10 h-10 text-green-400 mb-3" />
                <h3 className="font-bold text-lg mb-2">Smart Notes</h3>
                <p className="text-sm text-slate-300">Auto-generated study guides</p>
              </div>
            </div>
          </motion.div>

          {/* Right — Auth Card (StuDocu-style) */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <Card className="bg-white/95 backdrop-blur-md p-8 rounded-3xl shadow-2xl relative overflow-hidden">
              <AnimatePresence mode="wait">
                {/* ════════ Social Auth View ════════ */}
                {authMode === 'social' && (
                  <motion.div
                    key="social"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.25 }}
                  >
                    <h2 className="text-3xl font-bold mb-2 text-center text-gray-900">
                      Welcome to GradeGuard
                    </h2>
                    <p className="text-gray-500 text-center mb-8 text-sm">
                      Sign in to start learning
                    </p>

                    {error && (
                      <div className="bg-destructive/10 text-destructive px-4 py-3 rounded-xl mb-4 text-sm" data-testid="auth-error">
                        {error}
                      </div>
                    )}

                    {/* Google Sign-In */}
                    <div className="space-y-3 mb-6">
                      <div className="google-login-wrapper" style={{ display: 'flex', justifyContent: 'center' }}>
                        <GoogleLogin
                          onSuccess={handleGoogleSuccess}
                          onError={handleGoogleError}
                          size="large"
                          width="100%"
                          text="continue_with"
                          shape="pill"
                          theme="outline"
                          data-testid="google-signin-button"
                        />
                      </div>

                      {/* Apple Sign-In (visual only — future implementation) */}
                      <button
                        className="w-full flex items-center justify-center gap-3 px-6 py-3.5 border border-gray-200 rounded-full
                                   text-gray-800 font-medium hover:bg-gray-50 transition-colors text-sm"
                        onClick={() => setError('Apple Sign-In coming soon!')}
                        data-testid="apple-signin-button"
                      >
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                        </svg>
                        Continue with Apple
                      </button>
                    </div>

                    {/* Divider */}
                    <div className="flex items-center gap-3 mb-6">
                      <div className="flex-1 h-px bg-gray-200" />
                      <span className="text-xs text-gray-400 whitespace-nowrap">or continue with email</span>
                      <div className="flex-1 h-px bg-gray-200" />
                    </div>

                    {/* Email toggle buttons (StuDocu-style: two side-by-side) */}
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => { resetForm(); setAuthMode('login'); }}
                        className="flex items-center justify-center gap-2 px-4 py-3 border border-gray-200 rounded-full
                                   text-gray-700 font-medium hover:bg-gray-50 transition-colors text-sm"
                        data-testid="email-signin-button"
                      >
                        <Mail className="w-4 h-4" />
                        Sign in with Email
                      </button>
                      <button
                        onClick={() => { resetForm(); setAuthMode('register'); }}
                        className="flex items-center justify-center gap-2 px-4 py-3 border border-gray-200 rounded-full
                                   text-gray-700 font-medium hover:bg-gray-50 transition-colors text-sm"
                        data-testid="email-register-button"
                      >
                        <Mail className="w-4 h-4" />
                        Register with Email
                      </button>
                    </div>
                  </motion.div>
                )}

                {/* ════════ Email Auth View (Login or Register) ════════ */}
                {(authMode === 'login' || authMode === 'register') && (
                  <motion.div
                    key="email"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.25 }}
                  >
                    {/* Back button */}
                    <button
                      onClick={() => { resetForm(); setAuthMode('social'); }}
                      className="flex items-center gap-1 text-gray-500 hover:text-gray-700 transition-colors text-sm mb-4"
                      data-testid="back-to-social"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Back
                    </button>

                    <h2 className="text-3xl font-bold mb-2 text-center text-gray-900">
                      {authMode === 'login' ? 'Welcome Back' : 'Join GradeGuard'}
                    </h2>
                    <p className="text-gray-500 text-center mb-6 text-sm">
                      {authMode === 'login' ? 'Sign in with your email' : 'Create your account'}
                    </p>

                    {error && (
                      <div className="bg-destructive/10 text-destructive px-4 py-3 rounded-xl mb-4 text-sm" data-testid="auth-error">
                        {error}
                      </div>
                    )}

                    <form onSubmit={handleEmailAuth} className="space-y-4">
                      {/* Name & University (register only) */}
                      {authMode === 'register' && (
                        <>
                          <div>
                            <label className="block text-sm font-medium mb-1.5 text-gray-700">Full Name</label>
                            <Input
                              type="text"
                              value={name}
                              onChange={(e) => setName(e.target.value)}
                              placeholder="Your name"
                              data-testid="name-input"
                              className="w-full rounded-xl"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-1.5 text-gray-700">University / School</label>
                            <Input
                              type="text"
                              value={university}
                              onChange={(e) => setUniversity(e.target.value)}
                              placeholder="e.g. McGill University"
                              data-testid="university-input"
                              className="w-full rounded-xl"
                            />
                          </div>
                        </>
                      )}

                      <div>
                        <label className="block text-sm font-medium mb-1.5 text-gray-700">Email</label>
                        <Input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="student@university.edu"
                          required
                          data-testid="email-input"
                          className="w-full rounded-xl"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1.5 text-gray-700">Password</label>
                        <Input
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="••••••••"
                          required
                          data-testid="password-input"
                          className="w-full rounded-xl"
                        />
                      </div>

                      <Button
                        type="submit"
                        disabled={loading}
                        data-testid="auth-submit-button"
                        className="w-full bg-accent hover:bg-accent/90 text-white py-6 rounded-full text-lg font-bold shadow-lg hover:shadow-xl transition-all"
                      >
                        {loading ? 'Loading...' : (authMode === 'login' ? 'Sign In' : 'Create Account')}
                      </Button>
                    </form>

                    {/* Toggle between login/register */}
                    <div className="mt-6 text-center">
                      <button
                        onClick={() => {
                          resetForm();
                          setAuthMode(authMode === 'login' ? 'register' : 'login');
                        }}
                        data-testid="toggle-auth-mode"
                        className="text-accent hover:text-accent/80 font-medium transition-colors text-sm"
                      >
                        {authMode === 'login' ? "Don't have an account? Register" : 'Already have an account? Sign in'}
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Landing;